// Public marketing program page — /programs/:slug
// Consumes GET /api/marketing/programs/:slug, which returns the
// content-as-data shape from VOYA9_Marketing_Footer_Pages_Content_Design.md.
// Members hit Sign In or Get Access in the header to move to the next stage.
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import { Eyebrow, Headline } from "@/components/Editorial";
import Button from "@/components/Button";
import TopWedge from "@/components/TopWedge";
import { Spinner } from "@/components/Loader";
import { C, PROGRAM_META, colorSchemeForProgram } from "@/lib/brand";
import { apiGet } from "@/lib/api";
import { getLocalProgramContent, mergeMarketingProgram } from "@/lib/marketingContent";
import { useMarketingSEO } from "@/lib/marketingSEO";
import MarketingProgramSections from "@/components/MarketingProgramSections";

// Build a placeholder coming-soon program from PROGRAM_META when the
// marketing API doesn't have a row for the slug yet AND we have no local
// JSON content. Lets footer / landing links to known-but-unseeded programs
// render a useful page (waitlist CTA) instead of the not-found error screen.
function placeholderFromMeta(slug) {
  const s = (slug || "").toLowerCase();
  const meta = PROGRAM_META.find((m) => m.slugMatch.some((x) => s.includes(x)));
  if (!meta) return null;
  return {
    slug: meta.slugMatch[0],
    name: `${meta.t}${meta.t2 ? " " + meta.t2 : ""}`,
    tagline: meta.q,
    description: null,
    categoryNumber: meta.num,
    status: "coming_soon",
    color: meta.color,
    accentColor: meta.dark,
    darkColor: meta.dark,
    isActive: false,
    _isPlaceholder: true,
  };
}

export default function MarketingProgram() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setProgram(null);
    setError(false);
    window.scrollTo({ top: 0 });
    const local = getLocalProgramContent(slug);
    apiGet(`/marketing/programs/${slug}`, { auth: false })
      .then((backend) => setProgram(mergeMarketingProgram(local, backend)))
      .catch(() => {
        if (local) return setProgram(local);
        const placeholder = placeholderFromMeta(slug);
        if (placeholder) setProgram(placeholder);
        else setError(true);
      });
  }, [slug]);

  // CMS MIGRATION: `program.seo` ships from the local JSON today; the
  // backend can override per-field via mergeMarketingProgram once the API
  // serves a `seo` block. The hook tolerates undefined and is a no-op until
  // content lands.
  useMarketingSEO({
    title: program?.seo?.title,
    description: program?.seo?.description,
    ogImage: program?.seo?.ogImage,
    canonicalUrl: program?.slug ? `https://voya9.com/programs/${program.slug}` : undefined,
  });

  if (error) {
    return (
      <div style={{ minHeight: "100vh", background: C.cream }}>
        <PublicNav />
        <div style={{ padding: "120px 24px", textAlign: "center", maxWidth: 540, margin: "0 auto" }}>
          <Eyebrow color={C.coral}>★ NOT FOUND</Eyebrow>
          <Headline before="WE COULDN'T" italic="find that program." size={48} className="mt-4" />
          <div style={{ marginTop: 32 }}>
            <Button variant="primary" onClick={() => navigate("/")} testId="program-not-found-home-btn">
              BACK TO HOME →
            </Button>
          </div>
        </div>
        <Footer prevColor={C.cream} />
      </div>
    );
  }

  if (!program) {
    return (
      <div style={{ minHeight: "100vh", background: C.cream }}>
        <PublicNav />
        <div style={{ padding: 120, textAlign: "center" }}><Spinner /></div>
      </div>
    );
  }

  const scheme = colorSchemeForProgram(program);
  const isAvailable = (program.status || "").toLowerCase() === "available" || program.isActive === true;

  // Synthesize a hero block from the top-level summary fields when the
  // backend hasn't shipped the long-form `hero` content block yet.
  const hero = program.hero || {
    eyebrow: `PROGRAM ${program.categoryNumber || "01"} OF 06${isAvailable ? " · AVAILABLE NOW" : " · LAUNCHING SOON"}`,
    headlineLine1: (program.name || "").toUpperCase(),
    headlineLine2: program.tagline,
    subhead: program.description,
    primaryCta: { label: isAvailable ? "BEGIN YOUR ASSESSMENT →" : "JOIN THE WAITLIST →", url: isAvailable ? "/get-access" : "/get-access" },
    secondaryLine: isAvailable ? "Takes about 10 minutes · $49 physician review · cancel anytime" : "Founder pricing for early members",
  };

  // Synthesize a final CTA so the page always has an action even before
  // the long-form content arrives from the backend.
  const finalCta = program.finalCta || {
    headlineLine1: isAvailable ? "READY TO SEE" : "READY WHEN",
    headlineLine2: isAvailable ? "what's possible?" : "we are.",
    subhead: isAvailable ? "Most members get a physician response within 24 hours." : "We'll let you know the moment doors open.",
    cta: { label: isAvailable ? "BEGIN YOUR ASSESSMENT →" : "JOIN THE WAITLIST →", url: "/get-access" },
    footnote: isAvailable ? "$49 physician review · refunded if declined" : null,
  };

  return (
    <div data-testid={`marketing-program-${program.slug}`} style={{ background: C.cream, minHeight: "100vh" }}>
      <PublicNav />
      <Hero program={{ ...program, hero }} scheme={scheme} isAvailable={isAvailable} />
      <MarketingProgramSections program={program} />
      {program.pricing && <PricingTeaser block={program.pricing} />}
      <FinalCTA block={finalCta} scheme={scheme} isAvailable={isAvailable} />
      <BackToPrograms />
      <Footer prevColor={C.cream} />
    </div>
  );
}

// Sits between the per-program FinalCTA and the global Footer. Mirrors
// the "← BACK TO BROWSE" affordance the member-area program detail page
// shows, giving public visitors a quiet way to return to the program
// grid on the landing page after reading a "Learn more" page.
function BackToPrograms() {
  return (
    <section
      data-testid="marketing-back-to-programs"
      style={{ background: C.cream, padding: "48px 24px 64px", textAlign: "center" }}
    >
      <Link
        to="/#programs"
        style={{
          display: "inline-block",
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: "0.22em",
          color: C.ink,
          textDecoration: "none",
          borderBottom: `2px solid ${C.ink}`,
          paddingBottom: 4,
        }}
      >
        ← BACK TO PROGRAMS
      </Link>
    </section>
  );
}

function ctaTarget(cta, fallback = "/get-access") {
  return (cta && cta.url) || fallback;
}

function Hero({ program, scheme, isAvailable }) {
  const h = program.hero || {};
  const ctaUrl = ctaTarget(h.primaryCta, isAvailable ? "/get-access" : `#waitlist-${program.slug}`);
  return (
    <section
      style={{
        background: scheme.background,
        color: scheme.textPrimary,
        padding: "104px 24px 96px",
        position: "relative",
        // Numeral watermark is contained to the section. Body still has
        // overflow-x: clip independently so any other absolute children
        // can't cause horizontal page scroll.
        overflow: "hidden",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: -55,
          right: -20,
          zIndex: 1,
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontStyle: "italic",
          // 3× the previous scale per direction. clamp ensures it stays
          // proportional from iPhone SE up through 4K monitors. The
          // numeral is now decorative watermark territory — headline
          // sits on top via the content wrapper's zIndex: 2.
          fontSize: "clamp(280px, 48vw, 560px)",
          lineHeight: 1,
          opacity: 0.05,
          color: scheme.numberOverlay || scheme.textSecondary,
          // Instrument Serif ships single-weight only. We fake a thicker
          // weight via text-stroke — adds a 1px outline to every glyph.
          // Both webkit + standard properties for cross-browser support.
          WebkitTextStroke: `1px ${scheme.numberOverlay || scheme.textSecondary}`,
          textStroke: `1px ${scheme.numberOverlay || scheme.textSecondary}`,
          pointerEvents: "none",
          letterSpacing: "-0.04em",
        }}
      >
        {program.categoryNumber || "01"}
      </span>
      <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 2 }}>
        {h.eyebrow && (
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: scheme.textSecondary, textTransform: "uppercase" }}>
            ★ {h.eyebrow}
          </div>
        )}
        {(h.headlineLine1 || h.headlineLine2) && (
          <h1
            style={{
              marginTop: 22,
              fontSize: "clamp(44px, 8vw, 92px)",
              fontWeight: 800,
              letterSpacing: "-0.04em",
              lineHeight: 0.95,
              color: scheme.textPrimary,
              maxWidth: 920,
            }}
          >
            <span style={{ display: "block", textTransform: "uppercase" }}>{h.headlineLine1}</span>
            {h.headlineLine2 && (
              <span style={{ display: "block", fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic", fontWeight: 400 }}>
                {h.headlineLine2}
              </span>
            )}
          </h1>
        )}
        {h.subhead && (
          <p style={{ marginTop: 28, fontSize: 17, lineHeight: 1.55, maxWidth: 640, color: scheme.textPrimary, opacity: 0.85 }}>
            {h.subhead}
          </p>
        )}
        {h.primaryCta && (
          <div style={{ marginTop: 36 }}>
            <Link to={ctaUrl} data-testid={`program-${program.slug}-hero-cta`} style={{ textDecoration: "none" }}>
              <Button variant="primary" size="lg">{h.primaryCta.label || "BEGIN YOUR ASSESSMENT →"}</Button>
            </Link>
          </div>
        )}
        {h.secondaryLine && (
          <div style={{ marginTop: 16, fontSize: 13, color: scheme.textSecondary }}>
            {h.secondaryLine}
          </div>
        )}
      </div>
    </section>
  );
}

function EditorialBlock({ block, background = C.cream, color = C.ink, accent = C.peachDeep }) {
  // Retained locally — currently used only for FinalCTA / hero layouts.
  // The reusable section primitives live in MarketingProgramSections.
  return (
    <section style={{ background, color, padding: "96px 24px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        {block.eyebrow && <Eyebrow color={accent}>{block.eyebrow}</Eyebrow>}
        {(block.headlineLine1 || block.headline) && (
          <Headline
            before={block.headlineLine1 || block.headline}
            italic={block.headlineLine2 || ""}
            size={64}
            color={color}
            italicColor={accent}
            className="mt-6"
          />
        )}
        {block.body && (
          <div style={{ marginTop: 28, fontSize: 17, lineHeight: 1.7, color, whiteSpace: "pre-line", maxWidth: 720 }}>
            {block.body}
          </div>
        )}
      </div>
    </section>
  );
}

function PricingTeaser({ block }) {
  return (
    <section style={{ background: C.cream, padding: "72px 24px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
        {block.eyebrow && <Eyebrow color={C.peachDeep}>{block.eyebrow}</Eyebrow>}
        {block.body && (
          <div style={{ marginTop: 22, fontSize: 18, lineHeight: 1.65, color: C.ink, whiteSpace: "pre-line" }}>
            {block.body}
          </div>
        )}
        {block.cta && (
          <div style={{ marginTop: 28 }}>
            <Link to={ctaTarget(block.cta)} style={{ textDecoration: "none" }}>
              <Button variant="primary" size="lg">{block.cta.label || "BEGIN YOUR ASSESSMENT →"}</Button>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function FinalCTA({ block, scheme, isAvailable }) {
  return (
    <section style={{ background: scheme.background, color: scheme.textPrimary, padding: "96px 24px", textAlign: "center" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {(block.headlineLine1 || block.headline) && (
          <Headline
            before={block.headlineLine1 || block.headline}
            italic={block.headlineLine2 || ""}
            color={scheme.textPrimary}
            italicColor={scheme.textSecondary}
            size={64}
          />
        )}
        {block.subhead && (
          <p style={{ marginTop: 24, fontSize: 17, lineHeight: 1.55, color: scheme.textPrimary, opacity: 0.85 }}>
            {block.subhead}
          </p>
        )}
        <div style={{ marginTop: 36 }}>
          <Link
            to={ctaTarget(block.cta, isAvailable ? "/get-access" : "/get-access")}
            data-testid="program-final-cta"
            style={{ textDecoration: "none" }}
          >
            <Button variant="primary" size="lg">{(block.cta && block.cta.label) || (isAvailable ? "BEGIN YOUR ASSESSMENT →" : "JOIN THE WAITLIST →")}</Button>
          </Link>
        </div>
        {block.footnote && (
          <div style={{ marginTop: 16, fontSize: 12, color: scheme.textSecondary }}>{block.footnote}</div>
        )}
      </div>
    </section>
  );
}
